import { apiRequest } from './client'

/**
 * Exchange hub status shape stored in exchange_hub_status table.
 */
export interface ExchangeHubStatus {
  hubSubmissions: Record<string, { submitted: boolean; date?: string }>
  documentSignatures: Record<string, { signed: boolean; date?: string; by?: string }>
  escrowData: any | null
  calculatorResults: any | null
  identityVerified: boolean
}

/**
 * Response shape from GET /:exchangeId/wizard/:scope
 */
interface WizardStateResponse {
  hubData: any | null
  hubStatus?: any
  currentStep?: number
  percentComplete?: number
  lastSavedAt?: string
}

const DEFAULT_HUB_STATUS: ExchangeHubStatus = {
  hubSubmissions: {},
  documentSignatures: {},
  escrowData: null,
  calculatorResults: null,
  identityVerified: false,
}

export const wizardApi = {
  /**
   * Load hub data for a specific exchange + scope.
   * Returns null if no data exists.
   */
  async getHubData(exchangeId: string, scope: string): Promise<any | null> {
    const response = await apiRequest<WizardStateResponse>(
      `/exchanges/${exchangeId}/wizard/${scope}`
    )
    return response.hubData
  },

  /**
   * Save/upsert hub data for a specific exchange + scope.
   * Server does upsert on (exchange_id, scope).
   */
  async saveHubData(
    exchangeId: string,
    scope: string,
    hubData: any,
    currentStep?: number
  ): Promise<void> {
    await apiRequest(`/exchanges/${exchangeId}/wizard/${scope}`, {
      method: 'PUT',
      body: { hubData, currentStep },
    })
  },

  /**
   * Load exchange hub status (submissions, signatures, escrow, calculator).
   * Returns defaults if no row exists.
   */
  async getHubStatus(exchangeId: string): Promise<ExchangeHubStatus> {
    try {
      return await apiRequest<ExchangeHubStatus>(
        `/exchanges/${exchangeId}/hub-status`
      )
    } catch {
      return { ...DEFAULT_HUB_STATUS }
    }
  },

  /**
   * Partial merge update of exchange hub status.
   * Server deep-merges JSONB fields (hubSubmissions, documentSignatures).
   */
  async updateHubStatus(
    exchangeId: string,
    updates: Partial<ExchangeHubStatus>
  ): Promise<void> {
    await apiRequest(`/exchanges/${exchangeId}/hub-status`, {
      method: 'PATCH',
      body: updates,
    })
  },

  async resetDemo(exchangeId: string): Promise<{ demoResetAt: string }> {
    return apiRequest<{ demoResetAt: string }>(`/exchanges/${exchangeId}/hub-status/reset`, {
      method: 'POST',
    })
  },

  async getCalculatorResults(exchangeId: string): Promise<CalculatorResults | null> {
    return apiRequest<CalculatorResults | null>(`/exchanges/${exchangeId}/calculator-results`, {
      method: 'GET',
    }).catch(() => null)
  },

  async saveCalculatorResults(exchangeId: string, results: CalculatorResults): Promise<CalculatorResults> {
    return apiRequest<CalculatorResults>(`/exchanges/${exchangeId}/calculator-results`, {
      method: 'PUT',
      body: results,
    })
  },

  async getEscrow(exchangeId: string): Promise<EscrowState | null> {
    return apiRequest<EscrowState | null>(`/exchanges/${exchangeId}/escrow`, {
      method: 'GET',
    }).catch(() => null)
  },

  async saveEscrow(exchangeId: string, state: EscrowState): Promise<EscrowState> {
    return apiRequest<EscrowState>(`/exchanges/${exchangeId}/escrow`, {
      method: 'PUT',
      body: state,
    })
  },

  async getSignatures(exchangeId: string): Promise<SignatureRecord[]> {
    return apiRequest<SignatureRecord[]>(`/exchanges/${exchangeId}/signatures`, {
      method: 'GET',
    }).catch(() => [])
  },

  async recordSignature(
    exchangeId: string,
    documentType: string,
    signature: SignaturePayload,
  ): Promise<SignatureRecord> {
    return apiRequest<SignatureRecord>(`/exchanges/${exchangeId}/signatures/${documentType}`, {
      method: 'POST',
      body: signature,
    })
  },
}

export interface CalculatorResults {
  sellingPrice?: number
  adjustedBasis?: number
  replacementCost?: number
  netCashBoot?: number
  totalBoot?: number
  recognizedGain?: number
  deferredGain?: number
  basisInReplacement?: number
  input?: Record<string, unknown>
  results?: Record<string, unknown>
}

export interface EscrowState {
  exchangeFundsHeld: number | null
  exchangeFundsHeldAt: string | null
  exchangeFundsStatus: 'pending' | 'deposited' | 'disbursed'
}

export interface SignaturePayload {
  signatureMethod: 'typed' | 'drawn' | 'docusign'
  signatureDataUrl?: string
  docusignEnvelopeId?: string
}

export interface SignatureRecord extends SignaturePayload {
  documentType: string
  signedAt: string
  signedByUserId: string
  signedByName: string
}
